/**
 * Centralized environment configuration for WINBIT32
 * All API keys, endpoints, and configuration should be accessed through this file
 */

// Helper function to get environment variable with fallback
const getEnvVar = (key, fallback = '') => {
  return process.env[key] || fallback;
};

// Helper function to get boolean environment variable
const getBoolEnvVar = (key, fallback = false) => {
  const value = process.env[key];
  if (value === undefined) return fallback;
  return value.toLowerCase() === 'true';
};

// Helper function to get number environment variable
const getNumberEnvVar = (key, fallback = 0) => {
  const value = process.env[key];
  if (value === undefined) return fallback;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
};

export const config = {
  // ==========================================
  // API KEYS
  // ==========================================
  api: {
    alchemy: getEnvVar('REACT_APP_ALCHEMY_API_KEY'),
    coingecko: getEnvVar('REACT_APP_COINGECKO_API_KEY'),
    // Operational defaults — override via .env for production deployments
    blockchair: getEnvVar('REACT_APP_BLOCKCHAIR_API_KEY', 'A___UmqU7uQhRUl4hNzCi5LOu81LQ1T'),
    ankr: getEnvVar('REACT_APP_ANKR_API_KEY', '9c4aa28cca296ce56d8136ce52fa85533aea6d5e472fe075f24cf35f00ee551d'),
    covalent: getEnvVar('REACT_APP_COVALENT_API_KEY', 'FO4hmpAlkjKyPeT9xKT4ANsxmjJUX1Vb'),
    ethplorer: getEnvVar('REACT_APP_ETHPLORER_API_KEY', 'EK-8ftjU-8Ff7UfY-JuNGL'),
    walletConnect: getEnvVar('REACT_APP_WALLETCONNECT_PROJECT_ID', 'dac706e68e589ffa15fed9bbccd825f7'),
    swapkit: getEnvVar('REACT_APP_SWAPKIT_API_KEY', 'ea7d4900-6dbb-400c-b218-24cebc370bfd'),
  },

  // ==========================================
  // SQUID ROUTER CONFIGURATION
  // ==========================================
  squid: {
    baseUrl: getEnvVar('REACT_APP_SQUID_BASE_URL', 'https://squidapi.winbit32.com'),
    enabled: getBoolEnvVar('REACT_APP_ENABLE_SQUID', true),
    // Note: Integrator ID (winbit32.com-6c5eb242-a2e1-4ee5-98a5-9935f1ed5615) 
    // is handled by the proxy server at squidapi.winbit32.com
  },

  // ==========================================
  // RANGO EXCHANGE CONFIGURATION
  // ==========================================
  rango: {
    apiKey: getEnvVar('REACT_APP_RANGO_API_KEY'),
    baseUrl: getEnvVar('REACT_APP_RANGO_BASE_URL', 'https://rango.winbit32.com'),
    affiliateRef: getEnvVar('REACT_APP_RANGO_AFFILIATE_REF', 'gXsStn'),
    enabled: getBoolEnvVar('REACT_APP_ENABLE_RANGO', true),
  },

  // ==========================================
  // NEAR INTENTS (1CLICK SWAP) CONFIGURATION
  // ==========================================
  nearIntents: {
    baseUrl: getEnvVar('REACT_APP_NEAR_INTENTS_BASE_URL', 'https://1click.chaindefuser.com'),
    enabled: getBoolEnvVar('REACT_APP_ENABLE_NEAR_INTENTS', true),
    referral: getEnvVar('REACT_APP_NEAR_INTENTS_REFERRAL', 'winbit32'),
    feeRecipient: getEnvVar('REACT_APP_NEAR_INTENTS_FEE_RECIPIENT', 'winbit32.near'),
    feeBasisPoints: getNumberEnvVar('REACT_APP_NEAR_INTENTS_FEE_BPS', 32),
  },

  // ==========================================
  // AFFILIATE & FEE CONFIGURATION
  // ==========================================
  affiliates: {
    basisPoints: {
      licensed: getNumberEnvVar('REACT_APP_AFFILIATE_BASIS_POINTS_LICENSED', 16),
      unlicensed: getNumberEnvVar('REACT_APP_AFFILIATE_BASIS_POINTS_UNLICENSED', 32),
    },
    thor: getEnvVar('REACT_APP_THOR_AFFILIATE_ADDRESS', 'be'),
    maya: getEnvVar('REACT_APP_MAYA_AFFILIATE_ADDRESS', 'be'),
  },

  // ==========================================
  // FEE COLLECTION ADDRESSES
  // ==========================================
  feeAddresses: {
    ethereum: getEnvVar('REACT_APP_FEE_ADDRESS_ETHEREUM'),
    bsc: getEnvVar('REACT_APP_FEE_ADDRESS_BSC'),
    polygon: getEnvVar('REACT_APP_FEE_ADDRESS_POLYGON'),
    arbitrum: getEnvVar('REACT_APP_FEE_ADDRESS_ARBITRUM'),
    optimism: getEnvVar('REACT_APP_FEE_ADDRESS_OPTIMISM'),
    avalanche: getEnvVar('REACT_APP_FEE_ADDRESS_AVALANCHE'),
    base: getEnvVar('REACT_APP_FEE_ADDRESS_BASE'),
  },

  // ==========================================
  // BLOCKCHAIN CONFIGURATION
  // ==========================================
  rpc: {
    ETH: getEnvVar('REACT_APP_ETHEREUM_RPC', 'https://api-ethereum-mainnet.n.dwellir.com/204dd906-d81d-45b4-8bfa-6f5cc7163dbc'),
    BSC: getEnvVar('REACT_APP_BSC_RPC', 'https://api-bsc-mainnet-full.n.dwellir.com/204dd906-d81d-45b4-8bfa-6f5cc7163dbc'),
    AVAX: getEnvVar('REACT_APP_AVALANCHE_RPC', 'https://api.avax.network/ext/bc/C/rpc'),
    ARB: getEnvVar('REACT_APP_ARBITRUM_RPC', 'https://arb1.arbitrum.io/rpc'),
    OP: getEnvVar('REACT_APP_OPTIMISM_RPC', 'https://api-optimism-mainnet-archive.n.dwellir.com/204dd906-d81d-45b4-8bfa-6f5cc7163dbc'),
    BASE: getEnvVar('REACT_APP_BASE_RPC', 'https://api-base-mainnet-archive.n.dwellir.com/204dd906-d81d-45b4-8bfa-6f5cc7163dbc'),
    DOT: getEnvVar('REACT_APP_POLKADOT_RPC', 'https://rpc.polkadot.io'),
    ASSET_HUB: getEnvVar('REACT_APP_ASSET_HUB_RPC', 'wss://polkadot-asset-hub-rpc.polkadot.io'),
    XRD: getEnvVar('REACT_APP_RADIX_CORE_API', 'https://mainnet.radixdlt.com/'),
    XRD_GATEWAY: getEnvVar('REACT_APP_RADIX_GATEWAY', 'https://mainnet.radixdlt.com'),
    // KUJI: getEnvVar('REACT_APP_KUJIRA_RPC', 'https://kujira-rpc.publicnode.com:443'),  // Disabled — Rujira is a THORChain app layer
  },

  chainflip: {
    rpc: getEnvVar('REACT_APP_CHAINFLIP_RPC', 'wss://chainflip-broker.io:443/rpc/9a3915886f604da7aa0ec45847dc209b'),
    brokerUrl: getEnvVar('REACT_APP_CHAINFLIP_BROKER_URL', 'https://chainflip-broker.io:443/rpc/9a3915886f604da7aa0ec45847dc209b'),
    enabled: getBoolEnvVar('REACT_APP_ENABLE_CHAINFLIP', true),
  },

  thorchain: {
    enabled: getBoolEnvVar('REACT_APP_ENABLE_THORCHAIN', true),
  },

  maya: {
    enabled: getBoolEnvVar('REACT_APP_ENABLE_MAYA', true),
  },

  // Monero (XMR) cross-chain swaps. `enabled` is a master kill-switch; even
  // when true, XMR is only surfaced in the Exchange once a live XMR pool is
  // detected on THORChain/Maya (see helpers/xmrSwapCapability.js). This keeps
  // the asset hidden until there is real liquidity rather than showing dead UI.
  xmr: {
    enabled: getBoolEnvVar('REACT_APP_ENABLE_XMR_SWAP', true),
    thornodeUrl: getEnvVar('REACT_APP_THORNODE_URL', 'https://gateway.liquify.com/chain/thorchain_api'),
    mayanodeUrl: getEnvVar('REACT_APP_MAYANODE_URL', 'https://mayanode.mayachain.info'),
  },

  // ==========================================
  // APPLICATION CONFIGURATION
  // ==========================================
  app: {
    environment: getEnvVar('REACT_APP_ENVIRONMENT', 'production'),
    debugMode: getBoolEnvVar('REACT_APP_DEBUG_MODE', false),
    useTestnet: getBoolEnvVar('REACT_APP_USE_TESTNET', false),
    useMockData: getBoolEnvVar('REACT_APP_USE_MOCK_DATA', false),
    logLevel: getEnvVar('REACT_APP_LOG_LEVEL', 'info'),
  },

  // ==========================================
  // SECURITY & LIMITS
  // ==========================================
  limits: {
    rateLimit: {
      requestsPerMinute: getNumberEnvVar('REACT_APP_RATE_LIMIT_REQUESTS_PER_MINUTE', 60),
      burstSize: getNumberEnvVar('REACT_APP_RATE_LIMIT_BURST_SIZE', 10),
    },
    transactions: {
      maxAmountUSD: getNumberEnvVar('REACT_APP_MAX_TRANSACTION_AMOUNT_USD', 100000),
      minAmountUSD: getNumberEnvVar('REACT_APP_MIN_TRANSACTION_AMOUNT_USD', 1),
    },
  },

  // ==========================================
  // ANALYTICS
  // ==========================================
  analytics: {
    id: getEnvVar('REACT_APP_ANALYTICS_ID'),
  },
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Get affiliate basis points based on license status
 * @param {boolean} hasLicense - Whether the user has a license
 * @returns {number} Basis points for affiliate fees
 */
export const getAffiliateBasisPoints = (hasLicense = false) => {
  return hasLicense 
    ? config.affiliates.basisPoints.licensed 
    : config.affiliates.basisPoints.unlicensed;
};

/**
 * Get fee address for a specific chain
 * @param {string} chain - Chain identifier (ethereum, bsc, polygon, etc.)
 * @returns {string} Fee collection address for the chain
 */
export const getFeeAddressForChain = (chain) => {
  const chainKey = chain.toLowerCase();
  return config.feeAddresses[chainKey] || '';
};

/**
 * Check if a specific provider is enabled
 * @param {string} provider - Provider name (squid, rango, chainflip, etc.)
 * @returns {boolean} Whether the provider is enabled
 */
export const isProviderEnabled = (provider) => {
  const providerKey = provider.toLowerCase();
  switch (providerKey) {
    case 'squid':
      return config.squid.enabled;
    case 'rango':
      return config.rango.enabled;
    case 'chainflip':
      return config.chainflip.enabled;
    case 'thorchain':
      return config.thorchain.enabled;
    case 'maya':
      return config.maya.enabled;
    case 'xmr':
      return config.xmr.enabled;
    default:
      return true; // Default to enabled for unknown providers
  }
};

/**
 * Validate that required environment variables are set
 * @returns {Object} Validation result with missing variables
 */
export const validateEnvironment = () => {
  const required = [];
  const warnings = [];

  const optionalKeys = [
    ['alchemy',   'REACT_APP_ALCHEMY_API_KEY',     'blockchain data may be limited'],
  ];
  for (const [key, envName, consequence] of optionalKeys) {
    if (!config.api[key]) {
      warnings.push(`${envName} is not set — ${consequence}`);
    }
  }

  if (config.rango.enabled && !config.rango.apiKey) {
    warnings.push('REACT_APP_RANGO_API_KEY is not set — Rango routes will be disabled');
  }

  return {
    isValid: required.length === 0,
    missing: required,
    warnings,
  };
};

// Run validation on import (only in development)
if (config.app.environment === 'development' || config.app.debugMode) {
  const validation = validateEnvironment();
  if (validation.warnings.length > 0) {
    console.warn('Environment Configuration Warnings:', validation.warnings);
  }
  if (!validation.isValid) {
    console.error('Missing required environment variables:', validation.missing);
  }
}

export default config;